import React, { useState, useEffect } from 'react';
import { Button, Card, Chip, CircularProgress, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Col, Row } from 'react-bootstrap';
import { getTaxrockConfig, disconnectTaxrock } from 'actions/user';
import { Toast } from 'components/shared/Toaster';

/**
 * TaxRock integration card. On mount it fetches the connect-state from the backend
 * (GET /v1/taxrock/config). When not connected, it shows a Connect button that redirects the
 * browser to TaxRock's OAuth consent URL; the backend callback finishes the exchange and returns
 * to /settings/taxrock?taxrock=connected, at which point this form re-fetches and shows Connected.
 * When connected, it shows the connection status and a Disconnect action.
 */
function TaxRockForm() {
    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState(false);
    const [taxrockUrl, setTaxrockUrl] = useState('');
    const [disconnecting, setDisconnecting] = useState(false);

    useEffect(() => {
        fetchTaxrockConfig();
    }, []);

    const fetchTaxrockConfig = async () => {
        setLoading(true);
        try {
            const response = await getTaxrockConfig();
            const data = response?.response;
            setConnected(!!data?.connected);
            setTaxrockUrl(data?.url || '');
        } catch (error) {
            console.error('Error fetching TaxRock config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = () => {
        if (taxrockUrl) {
            window.location.href = taxrockUrl;
        }
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await disconnectTaxrock();
            Toast({ type: 'success', message: 'TaxRock disconnected. You can reconnect with another account.' });
            await fetchTaxrockConfig();
        } catch (error) {
            console.error('TaxRock disconnect failed:', error);
            Toast({ type: 'error', message: 'Failed to disconnect TaxRock. Please try again.' });
        } finally {
            setDisconnecting(false);
        }
    };

    return (
        <Card className='flex justify-center items-center cardHeight' style={{ position: 'relative' }}>
            {loading ? (
                <div className='d-flex align-items-center justify-content-center h-100'>
                    <CircularProgress size={48} color='primary' />
                </div>
            ) : (
                <Row className="p-2" style={{ width: '100%' }}>
                    <Col md={12} className="text-center" style={{ marginTop: '40px' }}>
                        {connected ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '10px',
                                    maxWidth: 420,
                                    margin: '0 auto',
                                    padding: '24px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 12,
                                    backgroundColor: 'var(--bg-canvas)',
                                }}
                            >
                                <CheckCircleIcon style={{ fontSize: 48, color: '#2e9d5b' }} />
                                <Typography variant="h6" style={{ fontWeight: 600, margin: 0 }}>
                                    TaxRock Connected
                                </Typography>
                                <Chip
                                    label="Active"
                                    size="small"
                                    style={{ backgroundColor: '#e6f4ea', color: '#1e7e42', fontWeight: 600 }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleDisconnect}
                                    disabled={disconnecting}
                                    style={{ marginTop: 4 }}
                                >
                                    {disconnecting ? <CircularProgress size={20} color="inherit" /> : 'Disconnect'}
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConnect}
                                disabled={!taxrockUrl}
                            >
                                Connect With My TaxRock Account
                            </Button>
                        )}
                    </Col>
                </Row>
            )}
        </Card>
    );
}

export default TaxRockForm;
